import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { Button, Segment, useToast } from "../../ui";
import { HubFormCard } from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import md from "../common/manageDesktop.module.css";

/** Временно скрыто для всех — код и API кассы сохранены. */
const SHOW_FISCAL_RECEIPTS = false;

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("ru-RU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

const TAX_OPTIONS = [
  { value: 0, label: "ОСН" },
  { value: 1, label: "УСН доход" },
  { value: 2, label: "УСН доход-расход" },
  { value: 3, label: "ЕНВД" },
  { value: 4, label: "ЕСН" },
  { value: 5, label: "Патент" },
];

const VAT_OPTIONS = [
  { value: "", label: "Без НДС" },
  { value: "0", label: "НДС 0%" },
  { value: "10", label: "НДС 10%" },
  { value: "20", label: "НДС 20%" },
];

const PROVIDER_OPTIONS = [
  { id: "none", label: "Выкл." },
  { id: "cloudkassir", label: "CloudKassir" },
  { id: "atol", label: "АТОЛ" },
];

const fieldStyle = {
  display: "block",
  width: "100%",
  marginTop: 6,
  padding: "12px 14px",
  borderRadius: 12,
  border: "1px solid var(--aa-border, rgba(128,128,128,0.35))",
  background: "var(--aa-surface-2, var(--color-surface-muted, rgba(0,0,0,0.2)))",
  color: "var(--aa-text, inherit)",
  fontSize: 16,
  boxSizing: "border-box",
};

export default function SettingsAcquiringPanel({ compact = false, desktop = false }) {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [acquiring, setAcquiring] = useState(null);
  const [tinkoffGuideOpen, setTinkoffGuideOpen] = useState(true);
  const [tinkoffVerify, setTinkoffVerify] = useState(null); // null | { ok, message }
  const [webhookCopied, setWebhookCopied] = useState(false);
  const [tinkoffForm, setTinkoffForm] = useState({
    terminal_key: "",
    password: "",
  });
  const [fiscalForm, setFiscalForm] = useState({
    fiscal_enabled: false,
    fiscal_provider: "none",
    fiscal_api_login: "",
    fiscal_api_secret: "",
    fiscal_inn: "",
    fiscal_group_code: "",
    fiscal_taxation_system: 1,
    fiscal_vat_code: "",
    fiscal_company_email: "",
    fiscal_payment_address: "",
  });

  const applyFiscalFromAcquiring = (a) => {
    const f = a?.fiscal || {};
    setFiscalForm((prev) => ({
      ...prev,
      fiscal_enabled: Boolean(f.enabled),
      fiscal_provider: f.provider || "none",
      fiscal_api_login: "",
      fiscal_api_secret: "",
      fiscal_inn: f.inn || "",
      fiscal_group_code: f.group_code || "",
      fiscal_taxation_system: f.taxation_system ?? 1,
      fiscal_vat_code: f.vat_code == null ? "" : String(f.vat_code),
      fiscal_company_email: f.company_email || "",
      fiscal_payment_address: f.payment_address || "",
    }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clinicAPI.getClubAcquiring();
      const a = res.acquiring || null;
      setAcquiring(a);
      applyFiscalFromAcquiring(a);
      setTinkoffForm({ terminal_key: "", password: "" });
      setTinkoffVerify(null);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось загрузить статус оплаты");
      setAcquiring(null);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const status = searchParams.get("yookassa");
    if (!status) return;
    if (status === "connected") {
      toast.success("ЮKassa подключена");
      load();
    } else if (status === "error") {
      const reason = searchParams.get("reason");
      const messages = {
        access_denied: "Доступ не выдан — ЮKassa не подключена",
        invalid_state: "Сессия подключения устарела, попробуйте снова",
        missing_code: "ЮKassa не вернула код — доступ не выдан",
        token_exchange: "Не удалось получить токен у ЮKassa",
        tenant_not_found: "Клуб не найден",
      };
      toast.error(messages[reason] || "Не удалось подключить ЮKassa");
      load();
    }
    const next = new URLSearchParams(searchParams);
    next.delete("yookassa");
    next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, toast, load]);

  useEffect(() => {
    setTinkoffGuideOpen(!Boolean(acquiring?.tinkoff?.connected));
  }, [acquiring?.tinkoff?.connected]);

  const connect = async () => {
    if (acquiring?.tinkoff?.connected) {
      if (
        !window.confirm(
          "Сейчас подключён T-Bank. Подключить ЮKassa? T-Bank будет отключён.",
        )
      ) {
        return;
      }
    }
    setBusy(true);
    try {
      const res = await clinicAPI.startYookassaOauth();
      if (!res.authorize_url) {
        toast.error("Не получен адрес авторизации ЮKassa");
        setBusy(false);
        return;
      }
      window.location.href = res.authorize_url;
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось начать подключение");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Отключить приём платежей через ЮKassa для этого клуба?")) {
      return;
    }
    setBusy(true);
    try {
      const res = await clinicAPI.disconnectYookassa();
      setAcquiring(res.acquiring || null);
      toast.success("ЮKassa отключена");
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось отключить");
    } finally {
      setBusy(false);
    }
  };

  const saveTinkoff = async () => {
    const key = tinkoffForm.terminal_key.trim();
    const password = tinkoffForm.password.trim();
    const already = Boolean(acquiring?.tinkoff?.connected);

    if (!already) {
      if (!key || !password) {
        toast.error("Укажите Terminal Key и пароль терминала");
        return;
      }
      if (acquiring?.yookassa?.connected || acquiring?.connected) {
        if (
          !window.confirm(
            "Сейчас подключена ЮKassa. Подключить T-Bank? ЮKassa будет отключена.",
          )
        ) {
          return;
        }
      }
    } else if (!key && !password) {
      toast.error("Введите новый ключ или пароль для обновления");
      return;
    }

    setBusy(true);
    try {
      const payload = {};
      if (key) payload.tinkoff_terminal_key = key;
      if (password) payload.tinkoff_password = password;
      const res = await clinicAPI.updateClubAcquiring(payload);
      setAcquiring(res.acquiring || null);
      setTinkoffForm({ terminal_key: "", password: "" });
      setTinkoffVerify(null);
      toast.success(already ? "Ключи T-Bank обновлены" : "T-Bank подключён");
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось сохранить ключи");
    } finally {
      setBusy(false);
    }
  };

  const disconnectTinkoff = async () => {
    if (!window.confirm("Отключить приём платежей через T-Bank для этого клуба?")) {
      return;
    }
    setBusy(true);
    try {
      const res = await clinicAPI.disconnectTinkoff();
      setAcquiring(res.acquiring || null);
      setTinkoffForm({ terminal_key: "", password: "" });
      setTinkoffVerify(null);
      toast.success("T-Bank отключён");
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось отключить");
    } finally {
      setBusy(false);
    }
  };

  const verifyTinkoff = async () => {
    const key = tinkoffForm.terminal_key.trim();
    const password = tinkoffForm.password.trim();
    const already = Boolean(acquiring?.tinkoff?.connected);

    if (!already && (!key || !password)) {
      toast.error("Сначала введите Terminal Key и пароль");
      return;
    }

    setBusy(true);
    setTinkoffVerify(null);
    try {
      const payload = {};
      if (key) payload.tinkoff_terminal_key = key;
      if (password) payload.tinkoff_password = password;
      const res = await clinicAPI.verifyTinkoff(payload);
      const message =
        res.message || "Подключение к T-Bank работает — ключи приняты банком";
      setTinkoffVerify({ ok: true, message });
      toast.success(message);
    } catch (err) {
      const message =
        err.response?.data?.error || "Ключи не приняты T-Bank";
      setTinkoffVerify({ ok: false, message });
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const toggleOnlinePayments = async (enabled) => {
    setBusy(true);
    try {
      const res = await clinicAPI.updateClubAcquiring({
        online_payments_enabled: enabled,
      });
      setAcquiring(res.acquiring || null);
      toast.success(
        enabled
          ? "Онлайн-оплата абонементов включена"
          : "Онлайн-оплата абонементов выключена",
      );
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  };

  const setProvider = (id) => {
    setFiscalForm((p) => ({
      ...p,
      fiscal_provider: id,
      fiscal_enabled: id !== "none",
    }));
  };

  const saveFiscal = async () => {
    setBusy(true);
    try {
      const payload = {
        fiscal_enabled:
          fiscalForm.fiscal_provider !== "none" && fiscalForm.fiscal_enabled,
        fiscal_provider: fiscalForm.fiscal_provider,
        fiscal_inn: fiscalForm.fiscal_inn,
        fiscal_group_code: fiscalForm.fiscal_group_code,
        fiscal_taxation_system: Number(fiscalForm.fiscal_taxation_system),
        fiscal_vat_code:
          fiscalForm.fiscal_vat_code === ""
            ? null
            : Number(fiscalForm.fiscal_vat_code),
        fiscal_company_email: fiscalForm.fiscal_company_email || undefined,
        fiscal_payment_address: fiscalForm.fiscal_payment_address || undefined,
      };
      if (fiscalForm.fiscal_api_login.trim()) {
        payload.fiscal_api_login = fiscalForm.fiscal_api_login.trim();
      }
      if (fiscalForm.fiscal_api_secret.trim()) {
        payload.fiscal_api_secret = fiscalForm.fiscal_api_secret.trim();
      }
      const res = await clinicAPI.updateClubAcquiring(payload);
      setAcquiring(res.acquiring || null);
      applyFiscalFromAcquiring(res.acquiring);
      toast.success(
        fiscalForm.fiscal_provider === "none"
          ? "Чеки выключены"
          : "Касса сохранена",
      );
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось сохранить кассу");
    } finally {
      setBusy(false);
    }
  };

  const fiscal = acquiring?.fiscal;
  const provider = fiscalForm.fiscal_provider;
  const yookassa = acquiring?.yookassa || {
    connected: Boolean(acquiring?.connected),
    oauth_configured: acquiring?.oauth_configured,
    shop_account_id: acquiring?.shop_account_id,
    is_test: acquiring?.is_test,
    connected_at: acquiring?.connected_at,
  };
  const tinkoff = acquiring?.tinkoff || { connected: false };
  const anyConnected = Boolean(
    acquiring?.any_provider_connected || yookassa.connected || tinkoff.connected,
  );
  const activeProviderLabel =
    acquiring?.provider === "yookassa"
      ? "ЮKassa"
      : acquiring?.provider === "tinkoff"
        ? "T-Bank"
        : null;

  if (loading) {
    return <p className={s.onboardingSheetHint}>Загрузка…</p>;
  }

  const masterToggle =
    anyConnected ? (
      <label className={s.settingsToggleRow}>
        <input
          type="checkbox"
          checked={acquiring?.online_payments_enabled !== false}
          disabled={busy}
          onChange={(e) => toggleOnlinePayments(e.target.checked)}
        />
        <span>
          <strong>Принимать онлайн-платежи</strong>
          {activeProviderLabel ? (
            <span className={s.docSectionHint} style={{ display: "block" }}>
              Сейчас используется {activeProviderLabel}. Можно подключить только
              один эквайринг — при смене другой отключится.
            </span>
          ) : null}
        </span>
      </label>
    ) : null;

  const yookassaBlock = (
    <>
      <p className={s.onboardingSheetHint} style={{ margin: 0 }}>
        Деньги за абонементы — на счёт вашего магазина ЮKassa.
        {!yookassa.connected
          ? " Подключение ЮKassa отключит T-Bank, если он был активен."
          : ""}
      </p>

      {!yookassa.oauth_configured ? (
        <p className={s.docSectionHint}>
          На сервере не заданы YOOKASSA_OAUTH_CLIENT_ID /
          YOOKASSA_OAUTH_CLIENT_SECRET.
        </p>
      ) : null}

      {yookassa.connected ? (
        <>
          <p className={s.docSectionHint} style={{ margin: 0 }}>
            Магазин #{yookassa.shop_account_id || "—"}
            {yookassa.is_test ? " · тестовый" : " · боевой"}
            {yookassa.connected_at
              ? ` · с ${formatDateTime(yookassa.connected_at)}`
              : ""}
          </p>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={disconnect}
          >
            Отключить ЮKassa
          </Button>
        </>
      ) : (
        <Button
          type="button"
          disabled={busy || !yookassa.oauth_configured}
          onClick={connect}
          className={compact ? aa.hubPrimaryCta : undefined}
          style={compact ? { width: "100%" } : undefined}
        >
          {busy ? "Переход…" : "Подключить ЮKassa"}
        </Button>
      )}
    </>
  );

  const tinkoffWebhookUrl =
    acquiring?.tinkoff_webhook_url || "https://ваш-backend/webhook/tinkoff";

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(tinkoffWebhookUrl);
      setWebhookCopied(true);
      toast.success("Ссылка скопирована");
      window.setTimeout(() => setWebhookCopied(false), 2000);
    } catch {
      toast.error("Не удалось скопировать — выделите ссылку вручную");
    }
  };

  const tinkoffSteps = [
    {
      id: "register",
      title: "Оформите интернет‑эквайринг",
      body: "Нужны ИП или юрлицо и расчётный счёт в Т‑Банке. Если эквайринг уже есть — пропустите шаг.",
      path: null,
      action: {
        href: "https://business.tbank.ru",
        label: "Открыть Т‑Бизнес",
      },
    },
    {
      id: "terminal",
      title: "Откройте настройки терминала",
      body: "Выберите рабочий терминал для реальных оплат или тестовый — для проверки.",
      path: ["Магазины", "Ваш магазин", "Терминалы", "Рабочий / Тестовый", "Настроить"],
    },
    {
      id: "keys",
      title: "Скопируйте ключи",
      body: "В настройках терминала найдите номер (Terminal Key) и пароль — вставьте их в поля ниже. У тестового и рабочего терминала ключи разные.",
      path: null,
    },
    {
      id: "webhook",
      title: "Вставьте Notification URL",
      body: "В настройках того же терминала укажите адрес HTTP‑уведомлений. Без него оплата может пройти, а абонемент не активируется.",
      path: null,
      webhook: true,
    },
    {
      id: "connect",
      title: "Подключите в Бай.Спорт",
      body: "Сохраните ключи кнопкой «Подключить T-Bank», затем нажмите «Проверить подключение». Деньги будут приходить на ваш счёт.",
      path: null,
    },
  ];

  const tinkoffBlock = (
    <div className={s.acquiringProviderBlock}>
      <div className={s.acquiringProviderHead}>
        <div>
          <p className={s.acquiringProviderLead}>
            Клиенты платят за абонементы онлайн — деньги приходят на ваш
            терминал Т‑Банка.
          </p>
          {!tinkoff.connected ? (
            <p className={s.acquiringProviderNote}>
              Можно подключить только один эквайринг: T‑Bank заменит ЮKassa.
            </p>
          ) : null}
        </div>
        <span
          className={
            tinkoff.connected ? s.acquiringStatusOn : s.acquiringStatusOff
          }
        >
          {tinkoff.connected ? "Подключено" : "Не подключено"}
        </span>
      </div>

      {tinkoff.connected ? (
        <div className={s.acquiringConnectedMeta}>
          Терминал {tinkoff.terminal_key_masked || "••••"}
          {tinkoff.connected_at
            ? ` · с ${formatDateTime(tinkoff.connected_at)}`
            : ""}
        </div>
      ) : null}

      <div className={s.acquiringGuideCard}>
        <button
          type="button"
          className={s.acquiringGuideToggle}
          aria-expanded={tinkoffGuideOpen}
          onClick={() => setTinkoffGuideOpen((v) => !v)}
        >
          <span className={s.acquiringGuideToggleMain}>
            <span className={s.acquiringGuideToggleIcon} aria-hidden>
              ?
            </span>
            <span className={s.acquiringGuideToggleText}>
              <span className={s.acquiringGuideToggleTitle}>
                Как подключить T‑Bank
              </span>
              <span className={s.acquiringGuideToggleSub}>
                {tinkoff.connected
                  ? "Инструкция на случай смены ключей"
                  : "5 шагов · около 5 минут"}
              </span>
            </span>
          </span>
          <span className={s.acquiringGuideChevron} aria-hidden>
            {tinkoffGuideOpen ? "▴" : "▾"}
          </span>
        </button>

        {tinkoffGuideOpen ? (
          <div className={s.acquiringGuideBody}>
            <div className={s.acquiringGuideQuick}>
              <a
                className={s.acquiringGuideQuickBtn}
                href="https://business.tbank.ru"
                target="_blank"
                rel="noopener noreferrer"
              >
                Т‑Бизнес
              </a>
              <button
                type="button"
                className={s.acquiringGuideQuickBtn}
                onClick={copyWebhookUrl}
                disabled={busy}
              >
                {webhookCopied ? "URL скопирован" : "Скопировать Notification URL"}
              </button>
              <a
                className={s.acquiringGuideQuickBtnGhost}
                href="https://www.tbank.ru/business/help/business-payments/internet-acquiring/how-use/terminal/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Справка банка
              </a>
            </div>

            <ol className={s.acquiringGuideSteps}>
              {tinkoffSteps.map((step, i) => (
                <li key={step.id} className={s.acquiringGuideStep}>
                  <div className={s.acquiringGuideStepRail} aria-hidden>
                    <span className={s.acquiringGuideStepNum}>{i + 1}</span>
                    {i < tinkoffSteps.length - 1 ? (
                      <span className={s.acquiringGuideStepLine} />
                    ) : null}
                  </div>
                  <div className={s.acquiringGuideStepCard}>
                    <strong className={s.acquiringGuideStepTitle}>
                      {step.title}
                    </strong>
                    <p className={s.acquiringGuideStepBody}>{step.body}</p>
                    {step.path?.length ? (
                      <div className={s.acquiringPath}>
                        {step.path.map((part, idx) => (
                          <span key={`${step.id}-${part}`} className={s.acquiringPathItem}>
                            {idx > 0 ? (
                              <span className={s.acquiringPathSep} aria-hidden>
                                ›
                              </span>
                            ) : null}
                            <span className={s.acquiringPathChip}>{part}</span>
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {step.action ? (
                      <a
                        className={s.acquiringStepAction}
                        href={step.action.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {step.action.label} →
                      </a>
                    ) : null}
                    {step.webhook ? (
                      <div className={s.acquiringWebhookBox}>
                        <div className={s.acquiringWebhookLabel}>
                          Notification URL
                        </div>
                        <code className={s.acquiringWebhookUrl}>
                          {tinkoffWebhookUrl}
                        </code>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={busy}
                          onClick={copyWebhookUrl}
                        >
                          {webhookCopied ? "Скопировано" : "Копировать URL"}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </div>

      <div className={s.acquiringFields}>
        <label className={s.acquiringField}>
          <span>Terminal Key</span>
          <input
            type="text"
            value={tinkoffForm.terminal_key}
            disabled={busy}
            placeholder={
              tinkoff.connected
                ? tinkoff.terminal_key_masked || "не менять — оставьте пустым"
                : "Номер терминала из ЛК"
            }
            onChange={(e) => {
              setTinkoffVerify(null);
              setTinkoffForm((p) => ({ ...p, terminal_key: e.target.value }));
            }}
            autoComplete="off"
          />
        </label>
        <label className={s.acquiringField}>
          <span>Пароль терминала</span>
          <input
            type="password"
            value={tinkoffForm.password}
            disabled={busy}
            placeholder={
              tinkoff.connected
                ? "не менять — оставьте пустым"
                : "Пароль рядом с номером терминала"
            }
            onChange={(e) => {
              setTinkoffVerify(null);
              setTinkoffForm((p) => ({ ...p, password: e.target.value }));
            }}
            autoComplete="new-password"
          />
        </label>
      </div>

      <div className={s.acquiringActions}>
        <Button type="button" disabled={busy} onClick={saveTinkoff}>
          {busy
            ? "Сохранение…"
            : tinkoff.connected
              ? "Обновить ключи"
              : "Подключить T-Bank"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={busy}
          onClick={verifyTinkoff}
        >
          Проверить подключение
        </Button>
        {tinkoff.connected ? (
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={disconnectTinkoff}
          >
            Отключить
          </Button>
        ) : null}
      </div>

      {tinkoffVerify ? (
        <p
          className={
            tinkoffVerify.ok ? s.acquiringVerifyOk : s.acquiringVerifyFail
          }
          role="status"
        >
          {tinkoffVerify.ok ? "✓ " : "✗ "}
          {tinkoffVerify.message}
        </p>
      ) : null}
    </div>
  );

  const fiscalBlock = (
    <>
      <p className={s.onboardingSheetHint} style={{ margin: 0 }}>
        Чек после оплаты клиента. Отдельно от эквайринга.
      </p>

      <div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
          Сервис кассы
        </div>
        <Segment
          options={PROVIDER_OPTIONS}
          value={provider}
          onChange={setProvider}
        />
      </div>

      {provider !== "none" ? (
        <>
          <label className={s.settingsToggleRow}>
            <input
              type="checkbox"
              checked={fiscalForm.fiscal_enabled}
              disabled={busy}
              onChange={(e) =>
                setFiscalForm((p) => ({
                  ...p,
                  fiscal_enabled: e.target.checked,
                }))
              }
            />
            <span>
              <strong>Пробивать чеки</strong>
              <span className={s.docSectionHint} style={{ display: "block" }}>
                {fiscal?.has_credentials
                  ? "Ключи уже сохранены"
                  : "Укажите ключи ниже"}
              </span>
            </span>
          </label>

          <label style={{ display: "block", fontSize: 14 }}>
            {provider === "cloudkassir" ? "Public ID" : "Логин АТОЛ"}
            <input
              type="text"
              value={fiscalForm.fiscal_api_login}
              disabled={busy}
              placeholder={
                fiscal?.has_credentials ? "не менять — оставьте пустым" : ""
              }
              onChange={(e) =>
                setFiscalForm((p) => ({
                  ...p,
                  fiscal_api_login: e.target.value,
                }))
              }
              style={fieldStyle}
              autoComplete="off"
            />
          </label>
          <label style={{ display: "block", fontSize: 14 }}>
            {provider === "cloudkassir" ? "API Secret" : "Пароль АТОЛ"}
            <input
              type="password"
              value={fiscalForm.fiscal_api_secret}
              disabled={busy}
              placeholder={
                fiscal?.has_credentials ? "не менять — оставьте пустым" : ""
              }
              onChange={(e) =>
                setFiscalForm((p) => ({
                  ...p,
                  fiscal_api_secret: e.target.value,
                }))
              }
              style={fieldStyle}
              autoComplete="new-password"
            />
          </label>
          <label style={{ display: "block", fontSize: 14 }}>
            ИНН
            <input
              type="text"
              value={fiscalForm.fiscal_inn}
              disabled={busy}
              inputMode="numeric"
              onChange={(e) =>
                setFiscalForm((p) => ({ ...p, fiscal_inn: e.target.value }))
              }
              style={fieldStyle}
            />
          </label>

          {provider === "atol" ? (
            <>
              <label style={{ display: "block", fontSize: 14 }}>
                Код группы ККТ
                <input
                  type="text"
                  value={fiscalForm.fiscal_group_code}
                  disabled={busy}
                  onChange={(e) =>
                    setFiscalForm((p) => ({
                      ...p,
                      fiscal_group_code: e.target.value,
                    }))
                  }
                  style={fieldStyle}
                />
              </label>
              <label style={{ display: "block", fontSize: 14 }}>
                Email компании
                <input
                  type="email"
                  value={fiscalForm.fiscal_company_email}
                  disabled={busy}
                  onChange={(e) =>
                    setFiscalForm((p) => ({
                      ...p,
                      fiscal_company_email: e.target.value,
                    }))
                  }
                  style={fieldStyle}
                />
              </label>
              <label style={{ display: "block", fontSize: 14 }}>
                Адрес расчётов
                <input
                  type="url"
                  value={fiscalForm.fiscal_payment_address}
                  disabled={busy}
                  placeholder="https://…"
                  onChange={(e) =>
                    setFiscalForm((p) => ({
                      ...p,
                      fiscal_payment_address: e.target.value,
                    }))
                  }
                  style={fieldStyle}
                />
              </label>
            </>
          ) : null}

          <label style={{ display: "block", fontSize: 14 }}>
            СНО
            <select
              value={fiscalForm.fiscal_taxation_system}
              disabled={busy}
              onChange={(e) =>
                setFiscalForm((p) => ({
                  ...p,
                  fiscal_taxation_system: Number(e.target.value),
                }))
              }
              style={fieldStyle}
            >
              {TAX_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: "block", fontSize: 14 }}>
            НДС
            <select
              value={fiscalForm.fiscal_vat_code}
              disabled={busy}
              onChange={(e) =>
                setFiscalForm((p) => ({
                  ...p,
                  fiscal_vat_code: e.target.value,
                }))
              }
              style={fieldStyle}
            >
              {VAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : (
        <p className={s.docSectionHint} style={{ margin: 0 }}>
          Выберите CloudKassir или АТОЛ выше.
        </p>
      )}

      <Button type="button" disabled={busy} onClick={saveFiscal} block={compact}>
        {busy ? "Сохранение…" : "Сохранить кассу"}
      </Button>
    </>
  );

  if (compact) {
    return (
      <>
        {masterToggle ? <HubFormCard title="Онлайн-оплата">{masterToggle}</HubFormCard> : null}
        <HubFormCard title="ЮKassa">{yookassaBlock}</HubFormCard>
        <HubFormCard title="T-Bank">{tinkoffBlock}</HubFormCard>
        {SHOW_FISCAL_RECEIPTS ? (
          <HubFormCard title="Чеки (CloudKassir / АТОЛ)">
            {fiscalBlock}
          </HubFormCard>
        ) : null}
      </>
    );
  }

  return (
    <div className={desktop ? md.desktopSettingsSection : s.settingsPanel}>
      <h2 style={{ marginTop: 0, fontSize: "1.125rem" }}>Онлайн-оплата</h2>
      {masterToggle ? (
        <div className={s.settingsDataCard} style={{ marginBottom: 16 }}>
          {masterToggle}
        </div>
      ) : null}
      <div className={s.settingsDataCard}>
        <strong>ЮKassa</strong>
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
          {yookassaBlock}
        </div>
      </div>
      <div className={s.settingsDataCard} style={{ marginTop: 16 }}>
        <strong>T-Bank эквайринг</strong>
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
          {tinkoffBlock}
        </div>
      </div>
      {SHOW_FISCAL_RECEIPTS ? (
        <div className={s.settingsDataCard} style={{ marginTop: 16 }}>
          <strong>Чеки</strong>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
            {fiscalBlock}
          </div>
        </div>
      ) : null}
    </div>
  );
}
